import React, { useState, useEffect } from "react";
import {
  Modal,
  Select,
  Input,
  Switch,
  Spin,
} from "antd";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import type { Application } from "../../../../core/data/redux/recruitment/types";

// Directly defining the lean statuses here for the macro modal
const LEAN_APPLICATION_STATUSES = [
  "received",
  "in_pipeline",
  "offered",
  "hired",
  "rejected",
  "withdrawn",
  "on_hold",
] as const;

const { TextArea } = Input;

const DEFAULT_CANDIDATE_MESSAGES: Record<string, string> = {
  applied:
    "Thank you for applying! We've received your application and our team will review it shortly.",
  submitted:
    "Your application has been successfully submitted. Our team will review it and get back to you soon.",
  under_review:
    "Your application is currently under review by our hiring team. We'll be in touch with an update shortly.",
  screening:
    "We'd like to schedule a brief screening call to learn more about your background. Please look out for a calendar invite.",
  shortlisted:
    "Great news — you've been shortlisted! We were impressed with your profile and would like to move you to the next stage.",
  interview_scheduled:
    "We're excited to invite you for an interview. Please find the details below and let us know if you need to reschedule.",
  technical_round:
    "Congratulations on progressing to the technical round! Please review the details below and prepare accordingly.",
  hr_round:
    "You've advanced to the HR round — well done! We look forward to getting to know you better.",
  final_round:
    "You've reached the final interview round — excellent work! Details for the session are included below.",
  offer_extended:
    "We're delighted to extend a job offer to you. Please review the details carefully and respond at your earliest convenience.",
  offer_accepted:
    "We're thrilled that you've accepted our offer! Welcome aboard — our HR team will reach out shortly with onboarding details.",
  offer_declined:
    "Thank you for considering us and for letting us know your decision. We appreciate your time and wish you the very best.",
  hired:
    "Congratulations! We're pleased to confirm your selection. Your onboarding journey begins now — our HR team will be in touch with next steps.",
  rejected:
    "Thank you for your interest and for the time you invested in our process. After careful consideration, we will not be moving forward at this time. We encourage you to apply for future opportunities.",
  withdrawn:
    "We've noted your withdrawal from this process. Thank you for your time and for considering us — we wish you all the best.",
  on_hold:
    "Your application is currently on hold while we finalise our hiring plans. We appreciate your patience and will reach out as soon as a decision is made.",
  not_suitable:
    "Thank you for applying. After reviewing your profile, we feel it may not be the best fit for this particular role at this time. We'll keep your details on file.",
  duplicate:
    "We noticed a duplicate application in our system. Your original application remains active and under review — no further action is needed.",
  blacklisted:
    "Thank you for your interest in our organisation. At this time, we are unable to move forward with your application.",
};

function getDefaultMessage(status: string): string {
  if (DEFAULT_CANDIDATE_MESSAGES[status])
    return DEFAULT_CANDIDATE_MESSAGES[status];
  const label = status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `Your application status has been updated to "${label}". Our team will be in touch with further details shortly.`;
}

export interface StatusUpdatePayload {
  applicationId: number;
  newStatus: string;
  notes?: string;
  candidateMessage?: string;
  sendEmailNotification: boolean;
}

interface StatusUpdateModalProps {
  open: boolean;
  application: Application | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (payload: StatusUpdatePayload) => void;
}

const formatStatusLabel = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const statusOpts = LEAN_APPLICATION_STATUSES.map((s) => ({
  value: s,
  label: formatStatusLabel(s),
}));

const Label: React.FC<{ text: string; required?: boolean }> = ({
  text,
  required,
}) => (
  <label
    style={{
      display: "block",
      fontSize: 11,
      fontWeight: 700,
      marginBottom: 5,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    }}>
    {text}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </label>
);

const Divider = () => (
  <div style={{ height: 1, background: "#f1f5f9", margin: "2px 0" }} />
);

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  open,
  application,
  loading,
  onCancel,
  onSubmit,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [newStatus, setNewStatus] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [candidateMessage, setCandidateMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const isValid = !!newStatus && newStatus !== application?.status;

  useEffect(() => {
    if (application && open) {
      const defaultStatus = (application as any)?.__defaultStatus;
      const initialStatus = defaultStatus || application.status || "";
      setNewStatus(initialStatus);
      setCandidateMessage(getDefaultMessage(initialStatus));
      setNotes("");
      setSendEmail(true);
      setFieldErrors({});
    }
  }, [application, open]);

  const handleStatusChange = (val: string) => {
    setNewStatus(val);
    setCandidateMessage(getDefaultMessage(val));
  };

  const handleOk = () => {
    if (!application) return;
    const errs: Record<string, string> = {};
    if (!newStatus || newStatus === application.status) {
      errs.newStatus = "Please select a new stage";
    }

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    onSubmit({
      applicationId: application.id,
      newStatus,
      notes: notes.trim() || undefined,
      candidateMessage: candidateMessage.trim() || undefined,
      sendEmailNotification: sendEmail,
    });
  };

  const currentLabel = (application?.status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <Modal
      open={open}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              flexShrink: 0,
              background: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
              border: "1px solid #c7d2fe",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <i
              className="ti ti-transfer"
              style={{ fontSize: 17, color: "#4f46e5" }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.25,
              }}>
              Update Application Stage
            </div>
            {application && (
              <div
                style={{
                  fontSize: 12,
                  color: "#94a3b8",
                  fontWeight: 400,
                  marginTop: 2,
                }}>
                {application.candidate?.full_name}
                {application.job?.job_title && (
                  <>
                    <span style={{ color: "#cbd5e1" }}> · </span>
                    {application.job.job_title}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      }
      onCancel={onCancel}
      onOk={handleOk}
      okText={
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <i className="ti ti-check" style={{ fontSize: 13 }} />
          Update Stage
        </span>
      }
      okButtonProps={{
        loading,
        style: {
          background: "#4f46e5",
          borderColor: "#4f46e5",
          borderRadius: 8,
          fontWeight: 600,
          height: 36,
          paddingInline: 20,
        },
      }}
      cancelButtonProps={{
        disabled: loading,
        style: { borderRadius: 8, height: 36 },
      }}
      width={620}
      destroyOnClose
      zIndex={1111}
      style={{ top: 40, maxHeight: "calc(100vh - 80px)" }}
      styles={{
        mask: {
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(15, 23, 42, 0.4)",
        },
        header: {
          padding: "16px 22px 14px",
          borderBottom: "1px solid #f1f5f9",
          marginBottom: 0,
        },
        body: {
          padding: "18px 22px",
          maxHeight: "calc(100vh - 80px - 70px - 64px)",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "#e2e8f0 transparent",
        },
        footer: {
          padding: "12px 22px",
          borderTop: "1px solid #f1f5f9",
          marginTop: 0,
        },
      }}>
      {application && (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "end",
              gap: 10,
              padding: "12px 14px",
              background: "#f8fafc",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
            }}>
            <div>
              <Label text="Current stage" />
              <div
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  padding: "0 10px",
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 7,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#475569",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}>
                {currentLabel}
              </div>
            </div>

            <div style={{ paddingBottom: 2, color: "#94a3b8" }}>
              <i
                className="ti ti-arrow-narrow-right"
                style={{ fontSize: 20 }}
              />
            </div>

            <div>
              <Label text="Move to" required />
              <Select
                style={{ width: "100%" }}
                status={fieldErrors.newStatus ? "error" : undefined}
                placeholder="Select a stage…"
                value={
                  newStatus && newStatus !== application.status
                    ? newStatus
                    : undefined
                }
                onChange={(val) => {
                  handleStatusChange(val);
                  setFieldErrors((e) => {
                    const { newStatus: _, ...rest } = e;
                    return rest;
                  });
                }}
                options={statusOpts}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
              {fieldErrors.newStatus && (
                <div style={{ color: "#ef4444", fontSize: 11, marginTop: 3 }}>
                  {fieldErrors.newStatus}
                </div>
              )}
            </div>
          </div>

          <Divider />

          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 6,
              }}>
              <Label text="Message to candidate" />
              {newStatus && (
                <button
                  type="button"
                  onClick={() =>
                    setCandidateMessage(getDefaultMessage(newStatus))
                  }
                  style={{
                    fontSize: 11,
                    color: "#4f46e5",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}>
                  <i className="ti ti-refresh" style={{ fontSize: 11 }} />
                  Reset default
                </button>
              )}
            </div>
            <TextArea
              rows={3}
              placeholder="This message is included in the candidate's notification email…"
              value={candidateMessage}
              onChange={(e) => setCandidateMessage(e.target.value)}
              maxLength={500}
              showCount
              style={{ fontSize: 13, resize: "none", borderRadius: 8 }}
            />
          </div>

          <div>
            <Label text="Internal notes" />
            <TextArea
              rows={2}
              placeholder="Private notes for your hiring team — not sent to the candidate…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={1000}
              showCount
              style={{ fontSize: 13, resize: "none", borderRadius: 8 }}
            />
          </div>

          <div
            onClick={() => setSendEmail((v) => !v)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "11px 14px",
              borderRadius: 10,
              cursor: "pointer",
              border: `1px solid ${sendEmail ? "#bbf7d0" : "#e2e8f0"}`,
              background: sendEmail ? "#f0fdf4" : "#f8fafc",
              transition: "background 0.2s, border-color 0.2s",
            }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  flexShrink: 0,
                  background: sendEmail ? "#dcfce7" : "#f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.2s",
                }}>
                <i
                  className="ti ti-mail"
                  style={{
                    fontSize: 16,
                    color: sendEmail ? "#16a34a" : "#94a3b8",
                    transition: "color 0.2s",
                  }}
                />
              </div>
              <div>
                <div
                  style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                  Send email notification
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>
                  Notify the candidate about this stage change
                </div>
              </div>
            </div>
            <Switch
              checked={sendEmail}
              onChange={setSendEmail}
              onClick={(_, e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default StatusUpdateModal;
